import { useState } from "react";
import type { MooChart as Chart } from "../../types/moo";
import { money } from "../../lib/finance";

const colors = [
  "#b9ef99",
  "#a6bafc",
  "#edbf8c",
  "#d2a9dc",
  "#81cdc6",
  "#e496a1",
  "#d5d88e",
  "#9bc4e6",
  "#b8aaa0",
  "#aec59e",
];
export default function MooChart({ chart }: { chart: Chart }) {
  const [selected, setSelected] = useState<number | null>(null);
  const slices = chart.bars.filter(
    (b) => Number.isFinite(b.value) && b.value > 0,
  );
  const total = slices.reduce((sum, b) => sum + b.value, 0);
  let angle = -Math.PI / 2;
  const pick = (index: number) =>
    setSelected((v) => (v === index ? null : index));
  return (
    <figure
      className={`moo-chart ${chart.kind === "pie" ? "moo-pie-chart" : ""}`}
    >
      <figcaption>{chart.title}</figcaption>
      {chart.kind === "pie" ? (
        total > 0 ? (
          <>
            <div className="moo-pie-total">
              <small>Total expenses</small>
              <strong>{money(total, chart.currency)}</strong>
            </div>
            <svg
              className="moo-pie"
              viewBox="0 0 240 240"
              role="group"
              aria-label={chart.title}
            >
              {slices.map((slice, index) => {
                const start = angle;
                angle += (slice.value / total) * Math.PI * 2;
                const end = angle,
                  radius = 108;
                const d = `M120 120 L${120 + radius * Math.cos(start)} ${120 + radius * Math.sin(start)} A${radius} ${radius} 0 ${end - start > Math.PI ? 1 : 0} 1 ${120 + radius * Math.cos(end)} ${120 + radius * Math.sin(end)} Z`;
                const props = {
                  fill: colors[index % colors.length],
                  stroke: "#172217",
                  strokeWidth: 3,
                  opacity: selected === null || selected === index ? 1 : 0.35,
                  role: "button",
                  tabIndex: 0,
                  "aria-pressed": selected === index,
                  "aria-label": `${slice.label}: ${money(slice.value, chart.currency)}, ${((slice.value / total) * 100).toFixed(1)}%`,
                  onClick: () => pick(index),
                  onKeyDown: (e: React.KeyboardEvent<SVGElement>) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      pick(index);
                    }
                  },
                };
                return slices.length === 1 ? (
                  <circle
                    key={slice.label}
                    cx="120"
                    cy="120"
                    r={radius}
                    {...props}
                  />
                ) : (
                  <path key={slice.label} d={d} {...props} />
                );
              })}
            </svg>
            <ul className="moo-pie-legend">
              {slices.map((slice, index) => (
                <li key={slice.label}>
                  <button
                    type="button"
                    aria-pressed={selected === index}
                    onClick={() => pick(index)}
                  >
                    <span
                      className="moo-pie-dot"
                      style={{ background: colors[index % colors.length] }}
                    />
                    <span>
                      {slice.label}
                      <small>{((slice.value / total) * 100).toFixed(1)}%</small>
                    </span>
                    <strong>{money(slice.value, chart.currency)}</strong>
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p>No expenses in this period.</p>
        )
      ) : chart.bars.length ? (
        chart.bars.map((b, i) => (
          <div key={i}>
            <div>
              <span>{b.label}</span>
              <strong>{money(b.value, chart.currency)}</strong>
            </div>
            <div className="moo-bar">
              <span
                style={{
                  width: `${Math.max(0, (b.value / Math.max(...chart.bars.map((v) => v.value), 1)) * 100)}%`,
                }}
              />
            </div>
          </div>
        ))
      ) : (
        <p>No matching expenses.</p>
      )}
    </figure>
  );
}
